import { useEffect, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { adminService, SalesUser, CreateSalesUserInput } from "../../services/admin/admin.service";
import { DataTable } from "../../components/ui/data-table";
import { toast } from "react-hot-toast";

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function SalesTeam() {
  const [users, setUsers] = useState<SalesUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [tempPassword, setTempPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingUser, setEditingUser] = useState<SalesUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<SalesUser | null>(null);

  const [createForm, setCreateForm] = useState<CreateSalesUserInput>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    isActive: true,
  });

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await adminService.getSalesTeam();
      setUsers(data);
    } catch {
      toast.error("Failed to load sales team");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreating(true);
      const result = await adminService.createSalesUser(createForm);
      setTempPassword(result.tempPassword);
      setShowCreateModal(false);
      setShowPasswordModal(true);
      setCreateForm({ firstName: "", lastName: "", email: "" });
      await load();
      toast.success("Sales user created");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await adminService.updateSalesUser(editingUser.id, editForm);
      setShowEditModal(false);
      setEditingUser(null);
      await load();
      toast.success("Sales user updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update user");
    }
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    try {
      await adminService.deleteSalesUser(deletingUser.id);
      setShowDeleteConfirm(false);
      setDeletingUser(null);
      await load();
      toast.success("Sales user deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete user");
    }
  };

  const handleResetPassword = async (user: SalesUser) => {
    try {
      const result = await adminService.resetSalesUserPassword(user.id);
      setTempPassword(result.tempPassword);
      setShowPasswordModal(true);
      await load();
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reset password");
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const openEdit = (user: SalesUser) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      isActive: user.isActive,
    });
    setShowEditModal(true);
  };

  const openDelete = (user: SalesUser) => {
    setDeletingUser(user);
    setShowDeleteConfirm(true);
  };

  const columns: ColumnDef<SalesUser>[] = [
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => (
        <span className="text-sm text-foreground font-medium">
          {row.original.firstName} {row.original.lastName}
        </span>
      ),
      meta: { width: "w-44" },
    },
    {
      id: "email",
      header: "Email",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{row.original.email}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
            row.original.isActive
              ? "bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300"
              : "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300"
          }`}
        >
          {row.original.isActive ? "Active" : "Inactive"}
        </span>
      ),
      meta: { width: "w-28" },
    },
    {
      id: "password",
      header: "Password",
      cell: ({ row }) =>
        row.original.mustChangePassword ? (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300">
            Pending Setup
          </span>
        ) : (
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300">
            Set
          </span>
        ),
      meta: { width: "w-32" },
    },
    {
      id: "createdAt",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(row.original.createdAt)}
        </span>
      ),
      meta: { width: "w-28" },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => openEdit(row.original)}
            className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted rounded-lg hover:bg-accent transition-colors"
          >
            Edit
          </button>
          <button
            onClick={() => handleResetPassword(row.original)}
            className="px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors"
          >
            Reset Password
          </button>
          <button
            onClick={() => openDelete(row.original)}
            className="px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
          >
            Delete
          </button>
        </div>
      ),
      meta: {
        width: "w-72",
        headerClassName: "text-right",
        cellClassName: "text-right",
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Sales Team</h1>
          <p className="text-sm text-muted-foreground">
            Manage sales team members who can access the leads dashboard.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-[#d7785b] text-white rounded-lg hover:bg-[#d7785b]/90 transition-colors text-sm font-medium"
        >
          Add Sales User
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <p className="text-sm text-muted-foreground">Total Members</p>
          <p className="text-2xl font-semibold text-foreground">{users.length}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <p className="text-sm text-muted-foreground">Active</p>
          <p className="text-2xl font-semibold text-green-600 dark:text-green-400">
            {users.filter((u) => u.isActive).length}
          </p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <p className="text-sm text-muted-foreground">Pending Password Setup</p>
          <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400">
            {users.filter((u) => u.mustChangePassword).length}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={users}
          isLoading={isLoading}
          emptyText='No sales team members yet. Click "Add Sales User" to get started.'
        />
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-lg font-semibold text-foreground mb-4">Add Sales User</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">First Name</label>
                <input
                  type="text"
                  value={createForm.firstName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, firstName: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/50 focus:border-[#d7785b]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Last Name</label>
                <input
                  type="text"
                  value={createForm.lastName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, lastName: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/50 focus:border-[#d7785b]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/50 focus:border-[#d7785b]"
                  required
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-muted-foreground bg-muted rounded-lg hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#d7785b] rounded-lg hover:bg-[#d7785b]/90 transition-colors disabled:opacity-50"
                >
                  {creating ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Temp Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-lg font-semibold text-foreground mb-2">Temporary Password</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Share this password with the user. It will only be shown once and expires in 3 days.
            </p>
            <div className="flex items-center gap-2 bg-muted border border-border rounded-lg p-3">
              <code className="flex-1 text-sm font-mono text-foreground break-all select-all">
                {tempPassword}
              </code>
              <button
                onClick={() => copyToClipboard(tempPassword)}
                className="px-3 py-1.5 text-xs font-medium text-[#d7785b] bg-orange-50 dark:bg-orange-500/10 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-500/20 transition-colors shrink-0"
              >
                Copy
              </button>
            </div>
            <button
              onClick={() => {
                setShowPasswordModal(false);
                setTempPassword("");
              }}
              className="w-full mt-4 px-4 py-2 text-sm font-medium text-white bg-[#d7785b] rounded-lg hover:bg-[#d7785b]/90 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-lg font-semibold text-foreground mb-4">Edit Sales User</h2>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">First Name</label>
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/50 focus:border-[#d7785b]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Last Name</label>
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/50 focus:border-[#d7785b]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/50 focus:border-[#d7785b]"
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="rounded border-border text-[#d7785b] focus:ring-[#d7785b]"
                />
                <label htmlFor="isActive" className="text-sm text-muted-foreground">Active</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-muted-foreground bg-muted rounded-lg hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#d7785b] rounded-lg hover:bg-[#d7785b]/90 transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && deletingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
            <h2 className="text-lg font-semibold text-foreground mb-2">Delete Sales User</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to delete{" "}
              <span className="font-medium">{deletingUser.firstName} {deletingUser.lastName}</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeletingUser(null); }}
                className="flex-1 px-4 py-2 text-sm font-medium text-muted-foreground bg-muted rounded-lg hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
